// SeaShells Care logo — built as inline SVG for crisp scaling and live-theming.
// Inspired by the original spiral / inner-shell mark from seashellcare.com,
// updated to the new deep-teal + soft-gold palette.

const Mark = ({ variant, mark, size }) => {
  const isLight = variant === "light";
  const stroke = isLight ? "#F5EDE0" : "#1F4E4A";
  const gold = "#C9A961";
  const h = size * 1.15;

  // Original concentric whorl mark
  if (mark === "whorl") {
    return (
      <svg width={size} height={h} viewBox="0 0 80 92" fill="none" aria-hidden="true" className="shrink-0">
        <path
          d="M40 4 C 60 16, 70 38, 70 56 C 70 76, 56 88, 40 88 C 24 88, 10 76, 10 56 C 10 38, 20 16, 40 4 Z"
          stroke={stroke} strokeWidth="2.2" strokeLinecap="round"
        />
        <path
          d="M40 16 C 54 26, 61 42, 61 56 C 61 70, 52 80, 40 80 C 28 80, 19 70, 19 56 C 19 42, 26 26, 40 16 Z"
          stroke={stroke} strokeOpacity="0.78" strokeWidth="1.6"
        />
        <path
          d="M40 28 C 49 35, 53 47, 53 56 C 53 65, 47 72, 40 72 C 33 72, 27 65, 27 56 C 27 47, 31 35, 40 28 Z"
          stroke={stroke} strokeOpacity="0.55" strokeWidth="1.4"
        />
        <ellipse cx="40" cy="56" rx="4" ry="6" fill={gold} />
        <path d="M40 4 L 40 16" stroke={gold} strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    );
  }

  // ROUNDED: softer circular pebble shell — nested rounded forms, no point at top
  if (mark === "rounded") {
    return (
      <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true" className="shrink-0">
        {/* Outer pebble */}
        <circle cx="40" cy="40" r="34" stroke={stroke} strokeWidth="2.4" />
        {/* Inner whorl: a soft, slightly offset rounded shape */}
        <path
          d="M40 14 C 56 14, 66 26, 66 42 C 66 58, 54 66, 40 66 C 28 66, 16 58, 16 44 C 16 30, 26 14, 40 14 Z"
          stroke={stroke} strokeOpacity="0.7" strokeWidth="1.8"
        />
        {/* Even softer inner */}
        <path
          d="M40 24 C 52 24, 58 33, 58 44 C 58 54, 50 60, 40 60 C 32 60, 24 54, 24 44 C 24 34, 30 24, 40 24 Z"
          stroke={stroke} strokeOpacity="0.45" strokeWidth="1.6"
        />
        {/* Gold pearl */}
        <circle cx="40" cy="44" r="5" fill={gold} />
      </svg>
    );
  }

  // SINGLE-CURVE: minimal, modern. A single sweeping arc forming an abstract shell silhouette
  if (mark === "curve") {
    return (
      <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true" className="shrink-0">
        {/* One continuous fluid arc spiralling inward (single stroke) */}
        <path
          d="M68 40 C 68 22, 54 8, 36 8 C 18 8, 6 22, 6 40 C 6 56, 18 70, 36 70 C 50 70, 60 60, 60 46 C 60 36, 52 28, 40 28 C 30 28, 24 34, 24 42"
          stroke={stroke}
          strokeWidth="3"
          strokeLinecap="round"
          fill="none"
        />
        {/* Tiny gold pearl at the spiral's resting point */}
        <circle cx="24" cy="42" r="3.4" fill={gold} />
      </svg>
    );
  }

  return null;
};

const Logo = ({
  variant = "dark", // "dark" (light bg) | "light" (dark bg)
  mark = "whorl",   // "whorl" | "rounded" | "curve"
  withStrapline = false,
  size = "md",      // sm | md | lg
  className = "",
  testId = "logo",
}) => {
  const sizes = {
    sm: { mark: 30, word: "text-lg",  letter: "tracking-[0.32em]" },
    md: { mark: 40, word: "text-2xl", letter: "tracking-[0.32em]" },
    lg: { mark: 64, word: "text-4xl", letter: "tracking-[0.36em]" },
  };
  const s = sizes[size] ?? sizes.md;
  const isLight = variant === "light";
  const inkColor = isLight ? "#F5EDE0" : "#1F4E4A";

  return (
    <span data-testid={testId} className={`inline-flex items-center gap-3 ${className}`}>
      <Mark variant={variant} mark={mark} size={s.mark} />
      <span className="leading-tight">
        <span
          className={`font-serif ${s.word} ${s.letter} uppercase font-medium block`}
          style={{ color: inkColor, paddingRight: "0.32em" }}
        >
          SeaShells
        </span>
        <span
          className="block"
          style={{ color: isLight ? "rgba(245,237,224,0.65)" : "#4a5a57" }}
        >
          {withStrapline ? (
            <span className="text-[9px] tracking-[0.28em] uppercase" style={{ paddingRight: "0.28em" }}>
              Your Care · Your Home · Our Commitment
            </span>
          ) : (
            <span className="text-[10px] tracking-[0.28em] uppercase" style={{ paddingRight: "0.28em" }}>
              Care · Essex
            </span>
          )}
        </span>
      </span>
    </span>
  );
};

export default Logo;
